import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "configs/firebaseConfig";
import store from "store/store";

const MAX_MESSAGE_LENGTH = 2000;
const MAX_STACK_LENGTH = 4000;
const RATE_LIMIT_WINDOW_MS = 10_000;
const RATE_LIMIT_MAX_PER_WINDOW = 5;
const SESSION_MAX_WRITES = 20;

const seen = new Set(); // dedupe key -> already sent this session
let windowStart = Date.now();
let windowCount = 0;
let sessionCount = 0;

// Only log from real environments; local dev noise must not pollute the collection.
const isEnabled = () =>
  process.env.REACT_APP_ENV === "production" ||
  process.env.REACT_APP_ERROR_LOGGING === "on";

// Crash loops (e.g. a render error that retriggers on every re-render) can otherwise
// write hundreds of docs in seconds and burn through the Firestore free tier in one
// bad deploy. Two backstops: a per-window cap and a hard per-session cap.
const isRateLimited = () => {
  const now = Date.now();
  if (now - windowStart > RATE_LIMIT_WINDOW_MS) {
    windowStart = now;
    windowCount = 0;
  }
  windowCount += 1;
  sessionCount += 1;
  return windowCount > RATE_LIMIT_MAX_PER_WINDOW || sessionCount > SESSION_MAX_WRITES;
};

const isDuplicate = (message, stack) => {
  const key = `${message}::${String(stack).slice(0, 200)}`;
  if (seen.has(key)) return true;
  seen.add(key);
  return false;
};

// Deterministic, non-cryptographic string hash (djb2) so repeat errors on the same
// route can be grouped/counted in the Firestore console without a backend.
const hashKey = (value) => {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

const getUser = () => {
  try {
    return store.getState()?.userDetails?.user ?? null;
  } catch {
    return null;
  }
};

const getUserInfo = () => {
  const user = getUser();
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const mobile = user?.mobile_number
    ? `${user?.country_code ? `+${String(user.country_code).replace(/^\+/, "")} ` : ""}${user.mobile_number}`
    : null;
  return {
    userId: user?.id ?? null,
    userName: name || null,
    userEmail: user?.email ?? null,
    userMobile: mobile,
  };
};

export const logFrontendError = async ({
  message,
  stack,
  componentStack = null,
  source,
  severity = "error",
  refId = null,
}) => {
  // "warning" is for non-critical, non-money-moving paths (e.g. a background
  // status poll failing) — worth a console trace for local debugging, but not
  // worth a Firestore write. Only "error"/"fatal" reach the collection.
  if (severity === "warning") {
    console.warn(`[${source}]`, message, stack);
    return;
  }

  if (!isEnabled()) return;
  if (isDuplicate(message, stack)) return;
  if (isRateLimited()) return;

  try {
    const route = window.location.pathname;
    await addDoc(collection(db, "frontendErrors"), {
      message: String(message).slice(0, MAX_MESSAGE_LENGTH),
      stack: stack ? String(stack).slice(0, MAX_STACK_LENGTH) : "",
      componentStack,
      source,
      severity,
      url: window.location.href,
      route,
      groupKey: hashKey(`${String(message).slice(0, MAX_MESSAGE_LENGTH)}::${route}`),
      userAgent: navigator.userAgent,
      environment: process.env.REACT_APP_ENV || "development",
      appVersion: process.env.REACT_APP_VERSION || null,
      ...getUserInfo(),
      refId,
      createdAt: serverTimestamp(),
    });
  } catch {
    // Logging must never crash the app it is trying to report on.
    // Swallow failures here (offline, Firestore down, rules rejection).
  }
};
